/**
 * transforms/vectors.ts — MINIMAL WRAPPER on three.js math (kinematic chain).
 *
 * REVISED BOUNDARY RULE (SPEC §6, point 2): `transforms/` Is the layer of
 * wrapping on three.js. Here it IS ALLOWED to import three.js: the SSOT of the
 *math is three.js (do NOT reimplement linear algebra by hand —
 * no custom QuatTuples, no homemade quaternionic products).
 *
 * Exposed functions (deterministic, stateless):
 * - `eulerDegToQuat(deg)` → THREE.Quaternion from euler XYZ in degrees
 * (ZYX composition: worldQ = qZ ∘ qY ∘ qX — exactly the template
 * Model3DPreview.calculateWorldQuaternion)
 * - `axisAngleQuat(axis, deg)` → THREE.Quaternion from axis + angle (degrees)
 * - `applyQuatToVec3(q, v)` → rotated Vec3 (tuple convenience)
 * - `sum(nums)` → pure numeric utility (preserved)
 *
 * Consumers (`model/*`) use THREE.Quaternion/THREE.Vector3/THREE.Object3D
 * directly: frame composition occurs via the parent-child HIERARCHY
 * native (matrixWorld), not with manual multiplications.
 *
 * Constrained 3D configurator: each function is commented for maintainability.*/

import * as THREE from "three";
import type { Vec3 } from "../data/types";

// ─── Assi canonici ──────────────────────────────────────────────

const AXIS_X = new THREE.Vector3(1, 0, 0);
const AXIS_Y = new THREE.Vector3(0, 1, 0);
const AXIS_Z = new THREE.Vector3(0, 0, 1);

/**
 * Quaternion from euler angles in DEGREES, order XYZ (ZYX composition:
 * worldQ = qZ ∘ qY ∘ qX) — exactly the template
 * Model3DPreview.calculateWorldQuaternion (`worldQ.multiply(qZ).multiply(qY).multiply(qX)`.
 *
 * @param eulerDeg Euler angles [x, y, z] in degrees (rotation.component_rotation_deg).
 * @returns THREE.Quaternion equivalent.*/
export function eulerDegToQuat(eulerDeg: Vec3): THREE.Quaternion {
  const q = new THREE.Quaternion();
  q.multiply(new THREE.Quaternion().setFromAxisAngle(AXIS_Z, THREE.MathUtils.degToRad(eulerDeg[2]))); // qZ
  q.multiply(new THREE.Quaternion().setFromAxisAngle(AXIS_Y, THREE.MathUtils.degToRad(eulerDeg[1]))); // qY
  q.multiply(new THREE.Quaternion().setFromAxisAngle(AXIS_X, THREE.MathUtils.degToRad(eulerDeg[0]))); // qX
  return q;
}

/**
 * Quaternion from axis + angle in DEGREES (right-hand rule), normalizing the axis.
 * Same behavior as THREE.Quaternion.setFromAxisAngle.
 *
 * @param axis Direction of the axis (will be normalized).
 * @param angleDeg Angle of rotation in degrees.
 * @returns THREE.Quaternion of the rotation.*/
export function axisAngleQuat(axis: Vec3, angleDeg: number): THREE.Quaternion {
  return new THREE.Quaternion().setFromAxisAngle(
    new THREE.Vector3(axis[0], axis[1], axis[2]).normalize(),
    THREE.MathUtils.degToRad(angleDeg),
  );
}

/**
 * Apply a THREE.Quaternion to a pure vector (tuple) and return the tuple
 * rotated. Convenience for points remaining in `Vec3` (pure data); where
 * working in three.js just `v.applyQuaternion(q)`.
 *
 * @param q Quaternion of rotation.
 * @param v Vector to rotate (tuple).
 * @returns Rotated vector (tuple).*/
export function applyQuatToVec3(q: THREE.Quaternion, v: Vec3): Vec3 {
  const out = new THREE.Vector3(v[0], v[1], v[2]).applyQuaternion(q);
  return [out.x, out.y, out.z];
}

/** Sum of a list of numbers (pure utility, PLAN §2.3).*/
export function sum(nums: number[]): number {
  return nums.reduce((acc, n) => acc + n, 0);
}
